import {
  Exposure,
  ExposureTrackingProvider,
  Experiment,
  AmplitudeUserProvider,
  ExperimentClient
} from '@amplitude/experiment-js-client';
import { JsonObj } from '@mhp/general-interface';
import axios, { AxiosInstance } from 'axios';
import amplitude from 'amplitude-js';
import crypto from 'crypto-js';
import Config from 'react-native-config';
import { Profile, Address, Insurance } from 'store/profile/types';
import DeviceInfoServices from './DeviceInfoServices';
import { AnalyticsUserAttributes, AmplitudeEventData } from './AnalyticsService.interfaces';

/**
 * All the analytics events supported by the DFD Application
 */
export enum AnalyticsEvent { //                                                 => 27 listed events should be covered by Page Viewed events...
  // Other
  PageViewed = 'Page Viewed',
  MobileAppBannerDownloadClicked = 'Mobile App Banner Download Clicked',
  MobileAppBannerCloseClicked = 'Mobile App Banner Close Clicked',
  AnnouncementsLinkClicked = 'Announcements Link Clicked',
  DashboardToDoCardClicked = 'Dashboard To-do Card Primary CTA Clicked',
  DashboardToDoCardDismissed = 'Dashboard To-do Card Dismissed',
  AnnouncementsCardDismissed = 'Announcements Card Dismissed',
  DashboardQuicklinksClicked = 'Dashboard Quicklinks Clicked',
  DashboardMessagesFetchError = 'Dashboard Messages Fetch Error',
  DashboardUpdatesFetchError = 'Dashboard Updates Fetch Error',
  DashboardBillingFetchError = 'Dashboard Billing Fetch Error',
  DashboardScheduleAppointmentClicked = 'Dashboard Schedule Appointment Clicked',
  DashboardCardDisplayed = 'Dashboard Card Displayed',

  // Messaging
  MessageSent = 'Message Sent', //                                              => event_properties include type, message_pool, provider_id?
  MessageRead = 'Message Read',
  NewMessageClicked = 'New Message Clicked',
  DownloadMessageDetailsClicked = 'Download Message Details Clicked',
  // Account Creation
  AccountCreationStarted = 'Account Creation Started',
  AccountCreationCompleted = 'Account Creation Completed',
  AccountCreationFlowExited = 'Account Creation Flow Exited',
  AccountCreationFormError = 'Account Creation Form Error',
  AccountCreationValidationCodeSent = 'Account Creation Validation Code Sent',
  AccountCreationSSNRequested = 'AccountCreationSSNRequested',
  AccountCreationValidationFailed = 'Account Creation Validation Failed',
  CallOnlineServicesClicked = 'Call Online Services Clicked',

  // Top Level Tab Navigation
  DashboardTabClicked = 'Dashboard Tab Clicked',
  TimelineTabClicked = 'Timeline Tab Clicked',
  HealthRecordTabClicked = 'Health Record Tab Clicked',
  GetCareTabClicked = 'Get Care Tab Clicked',
  BillingTabClicked = 'Billing Tab Clicked',
  MessagesTabClicked = 'Messages Tab Clicked',
  UnknownTabClicked = 'Unknown Tab Clicked',
  HealthToolsClicked = 'Health Tools Clicked',

  // Health Tools - Mental Health
  CredibleMindLaunched = 'CredibleMind Launched',

  // Book Appointment
  BookAppointmentStarted = 'Book Appointment Started',
  BookingPatientSelected = 'Booking Patient Selected',
  BookingProviderSelected = 'Booking Provider Selected',
  BookAppointmentCalendarViewed = 'Book Appointment Calendar Viewed',
  AppointmentTypeSelected = 'Appointment Type Selected',
  AppointmentTimeClicked = 'Appointment Time Clicked',
  BookAppointmentCallForCalendarOptions = 'Book Appointment Call for Calendar Options',
  BookAppointmentErrorCallForCalendarOptions = 'Book Appointment Error Call for Calendar Options',
  BookAppointmentConfirmationViewed = 'Book Appointment Confirmation Viewed',
  BookAppointmentCompleted = 'Book Appointment Completed',
  BookingFlowExited = 'Booking Flow Exited ',
  BookingHelpUsed = 'Booking Help Used ',
  CovidModalViewed = 'Covid Modal Viewed',
  CovidCallToRequestVVClicked = 'Covid Call To Request VV Clicked',
  CovidContinueToOnlineSchedulingClicked = 'Covid Continue To Online Scheduling Clicked',
  CovidMoreInformationClicked = 'Covid More Information Clicked',
  BookAppointmentErrorGoToCalendar = 'Book appointment error go to calendar',
  VideoVisitConsentCallError = 'Video visit consent service call error',

  // CovidScreens
  GetCareCovidTestClicked = 'Request Covid-19 Test Clicked',
  CovidTestGetStartedClicked = 'Covid-19 Screening Started',
  CovidTestPatientSelected = 'Covid-19 Patient Selected',
  CovidNext = 'Covid-19 Next Clicked',
  CovidPrev = 'Covid-19 Previous Clicked',
  CovidSubmit = 'Covid-19 Order Submitted',
  PhoneNumberClicked = 'Covid-19 Support Called',
  CovidFlowExited = 'Covid-19 Booking Flow Exited',
  CovidFacilitiesLinkClicked = 'Covid-19 Testing Facilities Accessed',
  CovidScreeningClosed = 'Covid-19 Screening Closed',
  CovidOrderCompleted = 'Covid-19 Order Completed',
  CovidOrderError = 'Covid-19 Error Tracking',
  CovidInsuranceCardUploadSkipped = 'Covid-19 Insurance Card Upload Skipped',
  CovidInsuranceUploadError = 'Covid-19 Insurance Card Upload Error',
  CovidAgreementCancelClicked = 'Covid-19 Screening Cancelled',
  CovidOTPError = 'Covid-19 OTP Failed to send',
  CovidOTPResend = 'Covid-19 OTP Resend Clicked',
  CovidOTPVerify = 'Covid-19 OTP Verify Clicked',
  CovidOTPDialogClose = 'Covid-19 OTP Dialog Closed',
  CovidContinueClicked = 'Covid-19 OTP Continue Clicked',
  CovidAdditionalTest = 'Covid-19 Additional test order initiated',
  CovidLanguageSelected = 'Language Selected',
  MyHealthAccountClicked = 'Covid-19 Create MyHealth+ Account Clicked',

  // Language Switcher
  LanguageSelected = 'Language Selected',
  LanguageSelectionError = 'Language Selection Failed',

  // VyncaScreens
  VyncaDashboardSelected = 'Vynca Advance Directive Clicked',
  VyncaScreenExited = 'Vynca Advance Directive Exited',
  VyncaPrevClicked = 'Vynca Previous Clicked',
  VyncaBeginClicked = 'Vynca Launch Clicked',
  VyncaFAQClicked = 'Vynca FAQs Clicked',
  VyncaFAQCategoryClicked = 'Vynca FAQ Category Clicked',
  VyncaFAQQuestionClicked = 'Vynca FAQ Question Clicked',

  // Prevent diabetes Screens
  PreventDiabetesDashboardClicked = 'Prediabetes and Diabetes Prevention Clicked',
  PreventDiabetesScreenExited = 'Prediabetes exited',
  PreventDiabetesLanguageSelected = 'Language Selected',
  PreventPrediabetesVideoSelected = 'Prediabetes Video Selected',
  PreventPrediabetesVideoStarted = 'Prediabetes Video Started',
  PreventPrediabetesVideoPaused = 'Prediabetes Video Paused',
  PreventPrediabetesVideoExited = 'Prediabetes Video Exited',
  PreventPrediabetesVideoCompleted = 'Prediabetes Video Completed',

  // Connect Care
  ConnectCareAccountCreation = 'Connect Care Account Creation',
  ConnectCareStart = 'Connect Care Start',
  ConnectCareWaitingRoomExit = 'Connect Care Waiting Room Exit',
  ConnectCareComplete = 'Connect Care Complete',
  ConnectCareTCContinueClicked = 'Connect Care TC Continue Clicked',
  ConnectCareAppointmentTCAgreeClicked = 'Connect Care Appointment TC Agree Clicked',
  ConsenttoTreatAccepted = 'Consent to Treat Accepted',
  MedicalHistoryEntered = 'Medical History Entered',
  MedicationsEntered = 'Medications Entered',
  VitalsEntered = 'Vitals Entered',
  InsuranceEntered = 'Insurance Entered',
  PaymentEntered = 'Payment Entered',
  SymptomsEntered = 'Symptoms Entered',
  ConnectCareFlowExited = 'Connect Care Flow Exited',
  ConnectCareProviderSelected = 'Connect Care Provider Selected',
  ConnectCarePreVisitFormsStart = 'Connect Care Pre-visit Forms Start',
  ConnectCarePreVisitFormsComplete = 'Connect Care Pre-visit Forms Complete',

  // Cost Estimation
  CostEstimatorStarted = 'Cost Estimator Started',
  CostEstimatorAppointmentPhoneClicked = 'Cost Estimator Appointment Phone Clicked',
  CostEstimateOptionSelected = 'Cost Estimate Option Selected',
  CostEstimatorCompleted = 'Cost Estimator Completed',

  // General
  SupportCalled = 'Support Called',
  SupportTicket = 'Support Ticket Submitted',
  FeedbackLeft = 'Feedback Left',
  MyHealthPlusStatusPageClicked = 'MyHealthPlusStatusPageClicked',
  AccountSwitched = 'Account Switched',
  AccountAccessReset = 'Account Access Reset',
  DisclaimerDeclined = 'Disclaimer Declined',
  DisclaimerAccepted = 'Disclaimer Accepted',
  RateAppClicked = 'Rate App Clicked',
  ExternalLinkClicked = 'External Link Clicked',
  shareIdeasClicked = 'Share Feedback Form Started',
  AccountViewAccessCodeSent = 'Account View Access Code Sent',
  AccountViewAccessCodeSentError = 'Account View Access Code Sent Error',
  AccountViewAccessCodeValidated = 'Account View Access Code Validated',
  AccountViewAccessCodeValidationFailed = 'Account View Access Code Validation Failed',
  AccountProfileOpened = 'Account Profile Opened',

  // Help
  ShareFeedbackClicked = 'Share Feedback Clicked',
  TechnicalSupportClicked = 'Technical Support Clicked',
  ContactUsClicked = 'Contact Us Clicked',
  AboutThisAppClicked = 'About This App Clicked',
  HelpTabClicked = 'Help Tab Clicked',
  HelpCenterClicked = 'Help Center Clicked',
  ShareFeedbackTechnicalSupportLinkClicked = 'Share Feedback Technical Support Link Clicked',
  ShareFeedbackFormSubmitted = 'Share Feedback Form Submitted',
  ShareFeedbackFormCanceled = 'Share Feedback Form Canceled',
  TechnicalSupportFormSubmitted = 'Technical Support Form Submitted',
  TechnicalSupportFormCanceled = 'Technical Support Form Canceled',
  ContactUsTechnicalSupportMessageSent = 'Contact Us Technical Support Message Sent',
  HealthToolsFeedBackSubmitted = 'Share Feedback Form Submitted',
  cancelHealthToolsFeedBackClicked = 'Share Feedback Form Canceled',

  // Get Care
  UrgentCareFacilityViewed = 'Urgent Care Facility Viewed', //                  => this should be covered by a nav-triggered 'Page Viewed' event
  UrgentCareFacilityCalled = 'Urgent Care Facility Called',
  UrgentCareFacilityAddressClicked = 'Urgent Care Facility Address Clicked',
  UrgentCareFacilityFiltered = 'Urgent Care Facility Filtered',
  UrgentCareFacilitySearched = 'Urgent Care Facility Searched',
  EmotionalSupportViewed = 'Emotional Support Viewed',

  // Ping (login)
  SignInTriggered = 'Sign In Triggered',
  SignInStarted = 'Sign In Started',
  SignInGuest = 'Sign In Guest',
  SignInCompleted = 'Sign In Completed',
  SignInFailed = 'Sign In Failed',
  SignInFailedTryAgainClicked = 'Sign In Failed Try Again Clicked',
  SignInFailedContactClicked = 'Sign In Failed Contact Clicked',
  SignInFailedStatusClicked = 'Sign In Failed Status Clicked',
  SignInFailedCloseClicked = 'Sign In Failed Close Clicked',

  // Manage Appointments
  ModifyAppointmentCompleted = 'Modify Appointment Completed',
  ModifyAppointmentStarted = 'Modify Appointment Started',
  UpcomingAppointmentSwiped = 'Upcoming Appointment Swiped',
  SeeAllPastAppoiintmentsClicked = 'See All Past Appointments Clicked',

  // Account
  MyDoctorsSwiped = 'My Doctors Swiped',
  SignedOut = 'Signed Out',
  MyAccountEditOptionsStarted = 'My Account Edit Options Started',
  MyAccountEditOptionsEdited = 'My Account Edit Options Edited',
  MyAccountEditOptionsCompleted = 'My Account Edit Options Completed',
  HIPAAAuthorization = 'HIPAA Authorization',
  HIPAARevocation = 'HIPAA Revocation',
  HIPAADocuments = 'HIPAA Documents',
  PUSHNotificationsToggled = 'Push Notifications Toggled',
  EMAILNotificationsToggled = 'Email Notifications Toggled',
  SMSNotificationsToggled = 'SMS Notifications Toggled',
  FCMNotificationsToggled = 'Push Notifications Toggled',
  MessagesNotificationsToggled = 'Notify Me Messages Toggled',
  AppointmentsNotificationsToggled = 'Notify Me Appointments Toggled',
  MedicationNotificationsToggled = 'Notify Me Medication Toggled',
  HealthRecordNotificationsToggled = 'Notify Me Health Record Toggled',
  HealthRecomendationsNotificationsToggled = 'Notify Me Health Recommendations Toggled',
  PaymentNotificationsToggled = 'Notify Me Payment Toggled',
  PromotionsNotificationsToggled = 'Notify Me Promotions Toggled',
  LoggedInPreferredPCP = 'Name_Of_PCP',
  AvatarSelectionStart = 'Avatar Selection Start',
  AvatarSelectionComplete = 'Avatar Selection Complete',
  InvalidEmailDialogClosedWithDialog = 'Invalid Email Dialog Closed With Click Outside Dialog',
  InvalidEmailDialogClosedWithX = 'Invalid Email Dialog Closed With X',
  InvalidEmailDialogClosedWithClose = 'Invalid Email Dialog Closed With Close',
  InvalidEmailDialogClosedWithUpdate = 'Invalid Email Dialog Closed With Update',
  GetNotificationsClicked = 'Notification prompt screen - Get notifications clicked',
  NoNotificationsClicked = 'Notification prompt screen - No notifications clicked',
  TwoStepLoginToggled = 'Two-Step Verification toggled',
  TwoStepLoginOn = 'Two-Step Verification turned on',
  TwoStepLoginOff = 'Two-Step Verification turned off',
  BlacklistedUserFailedOtp = 'Blacklisted User Failed OTP',

  // Provider Detail
  ProviderVideosViewed = 'Provider Videos Viewed', //                           => this should be covered by a nav-triggered 'Page Viewed' event
  ViewMoreProviderReviewsClicked = 'View More Provider Reviews Clicked',
  ProviderReviewsClicked = 'Provider Reviews Clicked',
  ProviderDetailViewed = 'Provider Detail Viewed', //                           => this should be covered by a nav-triggered 'Page Viewed' event
  ProviderPhoneNumberClicked = 'Provider Phone Number Clicked',
  ProviderNPVExceptionsViewed = 'Provider New Patient Visit Exceptions Clicked',

  // Provider Search
  ProviderSearchHomeSwiped = 'Provider Search Home Swiped',
  ProviderSearchStarted = 'Provider Search Started',
  ViewProviderSearchResultsClicked = 'View Provider Search Results Clicked',
  ProviderSearchTCContinueClicked = 'Provider Search TC Continue Clicked',
  ProviderSearchCompleted = 'Provider Search Completed',
  ProviderSearchResultsSorted = 'Provider Search Results Sorted',
  ViewMoreProviderSearchResultsClicked = 'View More Provider Search Results Clicked',
  ProviderSearchResultClicked = 'Provider Search Result Clicked', //            => event_properties: search_results_rank, search_results_page_number
  ProviderLocationsViewed = 'Provider Locations Viewed',

  // Symptom Checker (Triage)
  SymptomCheckerStarted = 'Symptom Checker Started',
  SymptomCheckerExited = 'Symptom Checker Exited ',
  SymptomCheckerCompleted = 'Symptom Checker Completed',
  SymptomCheckerActionTaken = 'Symptom Checker Action Taken',
  SymptomCheckerCovidSelected = 'Symptom Checker Covid Selected',
  DiagnosisProvided = 'Diagnosis Provided',
  CareOptionProvided = 'Care Option Provided',
  CareOptionSelected = 'Care Option Selected',

  // E-Visits
  EVisitStart = 'E-Visit Start',
  EVisitComplete = 'E-Visit Front Door Submitted',
  EVisitFrontDoorStart = 'E-Visit Front Door Started',
  EVisitFrontDoorSymptomSelected = 'E-Visit Front Door Symptom Selected',
  EVisitFrontDoorGyantTriageStart = 'E-Visit Front Door Gyant Triage Start',
  EVisitFrontDoorGyantAborted = 'E-Visit Front Door Triage Aborted',

  // E-Visits insurance
  EVisitsFrontDoorVerifyInsurance = 'E-Visit Front Door Verify Insurance',
  EVisitsFrontDoorInsuranceImageUploaded = 'E-Visit Front Door Add Insurance Image',
  EVisitsFrontDoorInsuranceSubmitted = 'E-Visit Front Door Insurance Submitted',
  // Manage Care/Health Records - Visit Summaries
  VisitSummariesClicked = 'Visit Summaries Clicked',
  ViewAllSummariesClicked = 'View All Summaries Clicked',
  VisitSummaryDetailClicked = 'Visit Summary Detail Clicked',
  DownloadVisitSummaryClicked = 'Download Visit Summary Clicked',
  ShareVisitSummaryClicked = 'Share Visit Summary Clicked',
  ClinicNotesItemClicked = 'Clinical Notes & Documents Item Clicked',
  ViewAllClinicNotesClicked = 'Clinical Notes & Documents View All Clicked',
  HealthRecordSummaryClicked = 'Health Record Summary Download Clicked',
  DownloadHealthRecordSummaryClicked = 'Health Record Download Summary Clicked',
  ShareHealthRecordSummaryClicked = 'Health Record Share Summary Clicked',
  CompleteCopyOfMedicalRecordMoreInformationClicked = 'Complete Copy of Medical Record More Information Clicked',

  // Manage Care/Health Records - Test Results
  TestResultsClicked = 'Test Results Clicked',
  TestResultsItemClicked = 'Test Results Item Clicked',
  ViewAllTestResultsClicked = 'View All Test Results Clicked',
  ShowMoreTestResultsClicked = 'Test Results Show More',
  DownloadTestResultsClicked = 'Download Test Results Clicked',
  ShareTestResultsClicked = 'Share Test Results Clicked',
  TestResultsDetailsViewed = 'Test Results Details Viewed',
  DownloadPatientLetterClicked = 'Download Patient Letter Clicked',
  SwitchLanguage = 'Switch Language',
  PromotionLink = 'Promotion Link',
  ErrorTracking = 'Error Tracking',

  // Manage Care/Health Records - Medical History
  MedicalHistoryClicked = 'Medical History Clicked',
  DownloadMedicalHistoryClicked = 'Medical History Download Clicked',
  ShareMedicalHistoryClicked = 'Medical History Share Clicked',
  ProcedureHistoryClicked = 'Procedure History Clicked',
  DownloadPastProceduresClicked = 'Download Past Procedures Clicked',
  SharePastProceduresClicked = 'Share Past Procedures Clicked',
  AllergiesClicked = 'Allergies Clicked',
  DownloadAllergiesClicked = 'Download Allergies Clicked',
  ShareAllergiesClicked = 'Share Allergies Clicked',
  SocialHistoryClicked = 'Social History Clicked',
  DownloadSocialHistoryClicked = 'Download Social History Clicked',
  ShareSocialHistoryClicked = 'Share Social History Clicked',
  SocialHistoryAlcoholClicked = 'Social History Alcohol Clicked',
  SocialHistorySubstanceUseClicked = 'Social History Substance Use Clicked',
  SocialHistoryTobaccoClicked = 'Social History Tobacco Clicked',
  SocialHistorySexualGenderClicked = 'Social History Sexual Gender Clicked',
  SocialHistoryHomeEnvironmentClicked = 'Social History Home Environment Clicked',
  SocialHistoryEmploymentSchoolClicked = 'Social History Employment School Clicked',
  SocialHistoryOtherClicked = 'Social History Other Clicked',
  ImmunizationsClicked = 'Immunizations Clicked',
  DownloadImmunizationsClicked = 'Download Immunizations Clicked',
  ShareImmunizationsClicked = 'Share Immunizations Clicked',
  ProblemListClicked = 'Problem List Clicked',
  DownloadProblemListClicked = 'Download Problem List Clicked',
  ShareProblemListClicked = 'Share Problem List Clicked',
  ProblemListActiveClicked = 'Problem List Active Clicked',
  ProblemListActiveItemClicked = 'Problem List Active Item Clicked',
  ProblemListActiveItemInfoClicked = 'Problem List Active Item Info Clicked',
  ProblemListResolvedClicked = 'Problem List Resolved Clicked',
  ProblemListResolvedItemClicked = 'Problem List Resolved Item Clicked',
  ProblemListResolvedItemInfoClicked = 'Problem List Resolved Item Info Clicked',
  ProblemListRemissionClicked = 'Problem List Remission Clicked',
  ProblemListRemissionItemClicked = 'Problem List Remission Item Clicked',
  ProblemListRemissionItemInfoClicked = 'Problem List Remission Item Info Clicked',
  UtahSchoolImmunizationRecordClicked = 'Utah School Immunization Record Clicked',
  ShareUtahSchoolImmunizationRecord = 'Share Utah School Immunization Record',
  DownloadUtahSchoolImmunizationRecord = 'Download Utah School Immunization Record',

  // Vitals
  VitalsClicked = 'Vitals Clicked',
  BlankVitalsBlockClicked = 'Blank Vitals Block Clicked',
  HeightLengthClicked = 'Height Clicked',
  WeightClicked = 'Weight Clicked',
  HeartRateClicked = 'Heart Rate Clicked',
  BloodPressureClicked = 'Blood Pressure Clicked',
  TemperatureClicked = 'Temperature Clicked',
  BMIClicked = 'BMI Clicked',
  HeadCircumferenceClicked = 'Head Circumference Clicked',
  DownloadVitalsClicked = 'Download Vitals Clicked',
  ShareVitalsClicked = 'Share Vitals Clicked',
  GoalsClicked = 'Goals Clicked',

  // Preventive Care
  PreventiveCareClicked = 'Preventive Care Clicked',
  PreventiveCareItemClicked = 'Preventive Care Item Clicked',

  // Education
  EducationClicked = 'Education Clicked',

  // Medications
  MedicationListClicked = 'Medication List Clicked',
  PreviousMedicationItemClicked = 'Previous Medication Item Clicked',
  CurrentMedicationItemClicked = 'Current Medication Item Clicked',
  PastMedicationsListViewed = 'Past Medications List Viewed',
  CurrentMedicationsListViewed = 'Current Medications List Viewed',
  RenewPrescriptionClicked = 'Renew Prescription Clicked',
  CurrentMedicationsListDownloadClicked = 'Current Medications List Download Clicked',
  CurrentMedicationsListShareClicked = 'Current Medications List Share Clicked',
  PastMedicationsListDownloadClicked = 'Past Medications List Download Clicked',
  PastMedicationsListShareClicked = 'Past Medications List Share Clicked',
  MedicationAskAQuestionClicked = 'Medication Ask a Question Clicked',
  MedicationMoreInfoClicked = 'Medication More Info Clicked',

  // Shared Messages
  ShareRecordsSent = 'Share Records Sent',

  // Billing screen (including drawers)

  ViewAndPayClicked = 'View and Pay Clicked', //                                >>> NEW: button in header carousel - opens another screen
  BillsClicked = 'Bills Clicked', //                                            open drawer
  IndividualBillingClicked = 'Individual Billing Clicked', //                   inside drawer action - opens another screen
  ViewAllBillsClicked = 'View All Bills Clicked', //                            inside drawer action - opens another screen
  PaymentHistoryClicked = 'Payment History Clicked', //                         open drawer
  IndividualPaymentClicked = 'Individual Payment Clicked', //                   inside drawer action - opens another screen
  ViewAllPaymentHistoryClicked = 'View All Payment History Clicked', //         inside drawer action - opens another screen
  CollectionsClicked = 'Collections Clicked', //                                open drawer
  ViewAllCollectionsClicked = 'View All Collections Clicked', //                inside drawer action - opens another screen
  LoadMoreCollectionsClicked = 'Load More Collections Clicked',
  PaymentPlanClicked = 'Payment Plan Clicked', //                               open drawer
  ApplyForAssistanceClicked = 'Apply for Financial Assistance Clicked', //      inside drawer action - opens another screen
  ViewAppStatusClicked = 'View Application Status Clicked', //                  inside drawer action - opens another screen
  EligibilityInformationClicked = 'Eligibility Information Clicked', //         inside drawer action - opens another screen
  FAQClicked = "FAQ's Clicked", //                                              inside drawer action - opens another screen
  BillPayComplete = 'Bill Pay Complete',
  BillPayRegistration = 'Bill Pay Registration',

  BillingBannerHelpCenter = 'Billing Banner-Billing Help Center',
  BillingBannerMoreInfo = 'Billing Banner-More information',
  BillingBannerWebPay = 'Billing Banner-Intermountain WebPay',
  BillingBannerEntripay = 'Billing Hompage-Entripay',

  // All Bills screen
  ProceedWithPaymentClicked = 'Proceed with Payment Clicked', //                inside drawer action - opens another screen
  // BillingApplyForFinancialAssistanceClicked = 'Billing Apply for Financial Assistance Clicked', // inside drawer action - opens another screen
  // ^^^ above event not triggered in mobile as of 4/30 11:30a
  // + Unpaid Bills items
  // ___ should fire existing IndividualBillingClicked, with type=unpaid? (Billing screen section)
  // + Paid Bills items
  // ___ should fire existing IndividualBillingClicked, with type=paid? (Billing screen section)
  // + See Eligibility Page link (SeeEligibilityPageClicked)
  // ___ should fire TBD - as of 4/30/20 there is no event associated with this action

  // Bill Detail screen
  // + Proceed with Payment button
  // ___ should fire existing ProceedwithPaymentClicked (All Bills screen section)
  // + Ask a Billing Question button - opens /u/billing/billing-questions
  AskBillingQuestionClicked = 'Ask a Billing Question Clicked',
  // + View Visit Summary link
  // ___ should fire TBD - as of 4/30/20 there is no event associated with this action
  // + Summary info icon link - opens Terms You Should Know modal (subset of Glossary)
  // ___ should fire TBD - as of 4/30/20 there is no event associated with this action
  // + Apply for Financial Assistance button
  // ___ should fire existing ApplyForAssistanceClicked (Billing screen section)
  ViewItemizedClicked = ' View Itemized Charges Clicked',
  ViewPaymentsClicked = ' View Payments and Adjustments Clicked',
  ViewClaimsClicked = ' View Claims Clicked',

  // Questions About Your Bill screen
  SubmitQuestionClicked = 'Submit Question Clicked',

  // Payment History screen
  // + Payment items
  // ___ should fire existing IndividualPaymentClicked (Billing screen section)
  // + Load More link (LoadMorePaymentItemsClicked?)
  // ___ should fire TBD - as of 4/30/20 there is no event associated with this action

  // Collections screen
  // + Collection items
  // ___ should fire existing IndividualBillingClicked, with type=collection? (Billing screen section)
  // + Load More link (LoadMoreCollectionItemsClicked?)
  // ___ should fire TBD - as of 4/30/20 there is no event associated with this action

  // Payment Plan screen
  // + 'X' in upper right corner to exit payment plan screen
  // ___ should fire TBD - as of 4/30/20 there is no event associated with this action
  // the rest of the screen appears to be Visitpay in an iFrame...

  // Financial Assistance screen
  StartApplicationClicked = 'Start Application Clicked',
  // + Frequently Asked Questions link
  // + 'See Eligibility Information for more details' link

  // Financial Assistance Application flow
  BasicInformationNextClicked = 'Basic Information Next Clicked',
  HouseholdNextClicked = 'Household Next Clicked',
  AssetsNextClicked = 'Assets Next Clicked',
  BillsNextClicked = 'Bills Next Clicked',
  RemoveBillClicked = 'Remove Bills Clicked',
  AddAdditionalMedExpenseClicked = 'Add Additional Medical Expense Clicked',
  FASubmitBtnClicked = 'Financial Assistance Submit Button Clicked',

  // Financial Assistance Application Status screen
  ApprovedStatusClicked = 'Approved Status Clicked', //                         => opens a new screen (Financial Assistance Application Status Details)
  DeniedStatusClicked = 'Denied Status Clicked',
  IncompleteStatusClicked = 'Incomplete Status Clicked',
  UnderReviewStatusClicked = 'Under Review Status Clicked',
  ReApplyClicked = 'Re-apply Clicked ',

  // Financial Assistance Application Status Details screen
  // + Pay Outstanding Bills button
  // ___ should fire TBD - as of 4/30/20 there is no event associated with this action

  // Glossary Events
  GlossaryClicked = 'Billing Glossary Clicked',

  // where are these events triggered from?
  AskQuestionClicked = 'Ask a Question Clicked ',

  SpendingSummaryClicked = 'Spending Summary Clicked', //                       => this is the same as View
  HSAClicked = 'Health Savings Account Clicked',
  HSAViewDetailsClicked = 'HSA View Detail Clicked',
  InsuranceCardClicked = 'Insurance Card Clicked',
  EstimateCostsClicked = 'Estimate Costs Clicked',
  SummaryTooltipClicked = 'Billing Summary Tooltip clicked',

  // check mobile to verify below event is covered by Page Viewed event...
  PaymentConfirmationDisplayed = 'Payment Confirmation Displayed', //           => this should be covered by a nav-triggered 'Page Viewed' event

  // Pre-registration
  HospitalFormStart = 'Hospital Form Start',
  HospitalFormDetailStart = 'Hospital Form Detail Start',
  HospitalFormDetailComplete = 'Hospital Form Detail Complete',
  HospitalFormDetailSkipped = 'Hospital Form Detail Skipped',
  HospitalFormPaymentStart = 'Hospital Form Payment Start',
  HospitalFormPaymentComplete = 'Hospital Form Payment Complete',
  HospitalFormViewBuildingMap = 'Hospital Form View Building Map',
  HospitalFormComplete = 'Hospital Form Complete',
  HospitalFormAuthFailed = 'Hospital Form Auth Failed',

  // Proxy Request Form
  ProxyAccessStart = 'Proxy Access Start',
  ProxyAccessComplete = 'Proxy Access Complete',
  ProxyAccessFailed = 'Proxy Access Failed',
  ProxyAccessEditedContactDetails = 'Proxy Access Edited Contact Details',
  ProxyAccessExitedEarly = 'Proxy Access Exited Early',

  // Anti-coagulation Response
  AnticoagulationResponse = 'Anticoagulation Response',

  // QuickSearch
  QuickSearchResultsClicked = 'Quick Search Results Clicked',
  QuickSearchAbandoned = 'Quick Search Abandoned'
}

export enum AnalyticsUserStat {
  AppointmentsBooked = 'Appointments Booked',
  AppointmentsModified = 'Appointments Modified',
  ConnectCareVisitsCompleted = 'Connect Care Visits Completed',
  ScheduledVideoVisitsCompleted = 'Scheduled Video Visits Completed',
  CostEstimatesCompleted = 'Cost Estimates Completed',
  NotificationsChecked = 'Notifications Checked', //                            => may be discarded if the next three can be implemented
  PushNotificationsChecked = 'PushNotifications Checked',
  EmailNotificationsChecked = 'EmailNotifications Checked',
  TextNotificationsChecked = 'TextNotifications Checked',
  PasswordResets = 'Password Resets', //                                        => cannot at this time be implemented inside DFD
  ProviderPagesSeen = 'Provider Pages Seen',

  NPSScore = 'NPS Score', //                                                    => Needs to be implemented based on to-be-defined internal app survey
  OverallSatisfaction = 'Overall Satisfaction' //                               => Needs to be implemented based on to-be-defined internal app survey
}

export enum AmplitudeProviderType {
  PrimaryCareProvider = 'Primary Care Provider',
  PastProvider = 'Past Provider',
  NewProvider = 'New Provider'
}

export interface TabNavigationEvents {
  [key: string]: string;
}

export const TabNavigationEventName: TabNavigationEvents = {
  HOME_ROOT: AnalyticsEvent.DashboardTabClicked,
  HEALTH_ROOT: AnalyticsEvent.HealthRecordTabClicked,
  CARE_ROOT: AnalyticsEvent.GetCareTabClicked,
  BILLING_ROOT: AnalyticsEvent.BillingTabClicked,
  HEALTH_TOOLS_ROOT: AnalyticsEvent.HealthToolsClicked
};

export interface ConfigOptions {
  useNativeDeviceInfo: boolean;
  includeReferrer?: boolean;
  includeUtm?: boolean;
  includeGclid?: boolean;
}

const getUserGender = (gender = '') => {
  switch (gender.toLowerCase()) {
    case 'male':
      return 'Male';
    case 'female':
      return 'Female';
    case 'other':
      return 'Other';
    default:
      return 'Unknown';
  }
};

const getUserInsurancePlan = (insurances?: Insurance[]) => {
  if (Array.isArray(insurances) && insurances.length) {
    return insurances[0].planName;
  }

  return '';
};

/**
 * Normalized for when arrays are potentially undefined
 */
const getArrayLength = <T>(field?: T[]) => {
  if (Array.isArray(field)) {
    return field.length;
  }

  return 0;
};

/**
 * Normalized for when a user is missing addresses
 */
const getUserZip = (addresses?: Address[]) => {
  if (Array.isArray(addresses) && addresses.length) {
    return addresses[0].postalCode;
  }

  return '';
};

export const getAnalyticsProviderType = (type?: 'pcp' | 'past') => {
  switch (type) {
    case 'pcp':
      return AmplitudeProviderType.PrimaryCareProvider;
    case 'past':
      return AmplitudeProviderType.PastProvider;
    default:
      return AmplitudeProviderType.NewProvider;
  }
};

/**
 * Given a user from our profile service, maps to the relevant Analytics Attributes
 */
const mapUserProfileToAnalyticsUser = (user: Profile): AnalyticsUserAttributes => {
  const { consumer, grantees, grantors, insurances, pcpProvider } = user;

  let isTestUser;
  if (consumer.lastName) {
    isTestUser = ['pxreg', 'xtest', 'xytest', 'xztest'].includes(consumer.lastName.toLowerCase());
  }

  return {
    userID: crypto.SHA256(consumer.consumerId).toString(),
    age: consumer.age,
    isAuthenticated: true,
    gender: getUserGender(consumer.gender),
    hasPrimaryCareProvider: getArrayLength(pcpProvider) > 0,
    insurancePlan: getUserInsurancePlan(insurances),
    lastSeenDate: new Date().toISOString(),
    numGrantors: getArrayLength(grantors),
    numGrantees: getArrayLength(grantees),
    zipCode: getUserZip(consumer.addresses),
    isTestUser
    // appInstallDate: getAppInstallDate() // we'll need to provide this fn in each AS file
  };
};

export class AmplitudeExposureTrackingProvider implements ExposureTrackingProvider {
  private readonly amp: amplitude.AmplitudeClient;

  constructor(amp: amplitude.AmplitudeClient) {
    this.amp = amp;
  }

  track(exposure: Exposure): void {
    this.amp.logEvent('$exposure', exposure);
  }
}

export interface SimpleResponse {
  status: number;
  body: string;
}
export interface HttpClient {
  request(
    requestUrl: string,
    method: string,
    headers: JsonObj,
    data: string,
    timeoutMillis?: number
  ): Promise<SimpleResponse>;
}

class AxiosHTTPClient implements HttpClient {
  axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create();
  }

  async request(
    requestUrl: string,
    method: string,
    headers: JsonObj,
    data: string,
    timeoutMillis?: number
  ): Promise<SimpleResponse> {
    const response = await this.axiosInstance.get(requestUrl, {
      headers,
      data,
      timeout: timeoutMillis
    });
    return {
      body: JSON.stringify(response.data),
      status: response.status
    } as SimpleResponse;
  }
}

export class AS {
  apiKey: string;
  amplitude: typeof amplitude;
  getDate: () => void;
  configOptions: ConfigOptions;
  experiment: ExperimentClient | undefined;

  constructor(
    apiKey: string,
    amplitudeModule: typeof amplitude,
    configOptions: ConfigOptions,
    getDate: () => void
  ) {
    this.apiKey = apiKey;
    this.amplitude = amplitudeModule;
    this.getDate = getDate;
    this.configOptions = configOptions;

    this.init();
  }

  get instance() {
    return this.amplitude.getInstance();
  }

  init = async () => {
    if (Config.AMPLITUDE_API_KEY && Config.APP_VERSION) {
      this.instance.init(Config.AMPLITUDE_API_KEY, undefined, this.configOptions);
      this.instance.setVersionName(Config.APP_VERSION);
      await new Promise(resolve => {
        this.instance.onInit(() => {
          resolve(null);
        });
      });
      this.experiment = Experiment.initialize(Config.AMPLITUDE_DEPLOYMENT_KEY, {
        userProvider: new AmplitudeUserProvider(this.instance),
        httpClient: new AxiosHTTPClient(),
        exposureTrackingProvider: new AmplitudeExposureTrackingProvider(this.instance)
        // @Note: Leaving this hear in case we need to debug in the near future
        // debug: true
      });
      await this.experiment.fetch();
    }
  };

  fetchExperimentVariant = (flagKey: string, fallback?: string) => {
    const result = this.experiment?.variant(flagKey, fallback);
    return result;
  };

  getExperimentStatus = (flagKey: string, variant: string) => {
    const result = this.fetchExperimentVariant(flagKey);
    return result?.value === variant;
  };

  identifyUser = (profile: Profile) => {
    const user = mapUserProfileToAnalyticsUser(profile);
    this.instance.setUserId(user.userID);

    const dimensions = DeviceInfoServices.getScreenDimensions();
    const screenResolution = `${dimensions.width}x${dimensions.height}`;

    const userIdentity = new amplitude.Identify()
      .set('Age', user.age)
      .set('Authentication Status', user.isAuthenticated)
      .set('Gender', user.gender)
      .set('Has Primary Care Provider', user.hasPrimaryCareProvider)
      .set('Insurance Plan', user.insurancePlan)
      .set('Last Seen Date', user.lastSeenDate)
      .set('People I Have Access To', user.numGrantors)
      .set('People Who Have Access To Me', user.numGrantees)
      .set('Zip Code', user.zipCode)
      .set('Screen Resolution', screenResolution)
      .add('Total Number Of Sessions', 1);

    // there are a number of test users in prod and we need to separate them
    if (user.isTestUser !== undefined) {
      userIdentity.set('Is Test User', user.isTestUser ? 'Yes' : 'No');
    }

    // This will only reliably be available on Android
    if (this.getDate) {
      userIdentity.set('App Install Date', this.getDate());
    }

    return this.instance.identify(userIdentity);
  };

  userPcItems = (hasPcItems: boolean): void => {
    const updateUserHasPcItems = new amplitude.Identify().set(
      'Has Preventive Care Items',
      hasPcItems
    );
    this.instance.identify(updateUserHasPcItems);
  };

  setUserAttribute = (attribute: string, value: string | number | JsonObj) => {
    this.instance.identify(new amplitude.Identify().set(attribute, value));
  };

  logEvent = (event: AnalyticsEvent, data?: Partial<AmplitudeEventData>): void => {
    this.instance.setVersionName(Config.APP_VERSION);
    this.instance.logEvent(event, data);
  };

  incrementUserStat = (stat: AnalyticsUserStat) => {
    this.instance.identify(new amplitude.Identify().add(stat, 1));
  };

  setUserStat = (stat: AnalyticsUserStat, value: string | number | JsonObj) => {
    this.instance.identify(new amplitude.Identify().set(stat, value));
  };

  clear = (): void => {
    // @ts-ignore this does accept null -> https://help.amplitude.com/hc/en-us/articles/115001361248#logging-out-and-anonymous-users
    this.instance.setUserId(null);
  };
}

export interface NotificationsEvents {
  [key: string]: string;
}

export const NotificationsEventNames: NotificationsEvents = {
  MESSAGE: AnalyticsEvent.MessagesNotificationsToggled,
  APPOINTMENT: AnalyticsEvent.AppointmentsNotificationsToggled,
  MEDICATION: AnalyticsEvent.MedicationNotificationsToggled,
  LABS: AnalyticsEvent.HealthRecordNotificationsToggled,
  ADVISORY: AnalyticsEvent.HealthRecomendationsNotificationsToggled,
  BILLING: AnalyticsEvent.PaymentNotificationsToggled,
  PROMOS: AnalyticsEvent.PromotionsNotificationsToggled
};

export type MentalHealthType = 'a' | 'b' | 'c' | 'd';

export const AMPLITUDE_EXPERIMENTS = {
  E_VISIT_FRONT_DOOR: {
    flagKey: 'e-visit-front-door',
    variantA: 'control',
    variantB: 'e-visit-front-door'
  },
  HEALTH_TOOLS_MENTAL_HEALTH: {
    flagKey: 'health-tools-mental-health-2',
    variantA: 'a',
    variantB: 'b'
  },
  VYNCA_SCREEN: {
    flagKey: 'vyncacard',
    variantA: 'control',
    variantB: 'treatment'
  },
  QUICK_SEARCH: {
    flagKey: 'quick_search',
    variantA: 'on',
    variantB: null
  },
  EMMI: {
    flagKey: 'emmi',
    variantA: 'on',
    variantB: null
  },
  E_VISIT_FRONT_DOOR_INSURANCE: {
    flagKey: 'e-visits-frontdoor-insurance',
    variantA: 'on',
    variantB: null
  },
  KYRUUS: {
    flagKey: 'kyruus',
    variantA: 'control',
    variantB: 'treatment'
  },
  WEBPAY_BANNER: {
    flagKey: 'billing-webpay-banner',
    variantA: 'on',
    variantB: null
  },
  ENTRIPAY_BANNER: {
    flagKey: 'billing-entripay-banner',
    variantA: 'on',
    variantB: null
  },
  NOTIFICATIONS_OPT_OUT_ALERT: {
    flagKey: 'notifications-opt-out-alert',
    variantA: 'on',
    variantB: null
  },
  TWO_STEP_LOGIN: {
    flagKey: 'two-step-login-verification-ping',
    variantA: 'on',
    variantB: null
  }
};
